import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../../components/MDDForm/components/ContentDialog.jsx";
import NewFormRenderer from "../../../../components/Form/FormDefinitionRenderer/NewFormRenderer.jsx";
import { FormPreviewModeContext } from "../../../../components/Form/FormDefinitionRenderer/contexts.js";
import { buildConditionDependents } from "../../../../utils/formTreeIds.js";
import { FORM_DEF_MAP } from "formdefs/formDefMap.js";

function ClaimFormPreviewContent({ claimForm }) {
  const definition = claimForm?.current_definition;

  const fileDef = definition?.definition_file_name
    ? FORM_DEF_MAP[definition.definition_file_name] ?? null
    : null;
  // rendered_definition has section wrappers already flattened server-side.
  const jsonDef = definition?.rendered_definition?.formdefinition
    ? definition.rendered_definition
    : null;

  const [source, setSource] = useState(fileDef ? "file" : "json");

  const activeDef = source === "file" ? fileDef : jsonDef;
  const previewValue = useMemo(
    () => ({ dependentsByField: buildConditionDependents(activeDef?.formdefinition) }),
    [activeDef]
  );

  // Field-level provenance is computed server-side from the raw (section-bearing)
  // definition; it only applies to the DB/JSON source.
  const provenance = definition?.section_provenance ?? [];
  const showProvenance = source === "json" && provenance.length > 0;

  if (!fileDef && !jsonDef) {
    return <Alert severity="info">Keine Formulardefinition hinterlegt.</Alert>;
  }

  return (
    <Box>
      {fileDef && jsonDef && (
        <ToggleButtonGroup
          value={source}
          exclusive
          onChange={(_, value) => value && setSource(value)}
          size="small"
          sx={{ mb: 3 }}
        >
          <ToggleButton value="file">
            Datei-Definition ({definition.definition_file_name})
          </ToggleButton>
          <ToggleButton value="json">JSON-Definition (DB)</ToggleButton>
        </ToggleButtonGroup>
      )}
      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FormPreviewModeContext.Provider value={previewValue}>
            <NewFormRenderer key={source} def={activeDef} model={{}} editMode={false} actions={[]} />
          </FormPreviewModeContext.Provider>
        </Box>
        {showProvenance && (
          <Box sx={{ flex: "0 0 260px", borderLeft: 1, borderColor: "divider", pl: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              Aus Section-Templates
            </Typography>
            {provenance.map((group, i) => (
              <Box key={`${group.key}-${i}`} sx={{ mb: 2 }}>
                <Chip size="small" label={group.key} sx={{ mb: 0.5 }} />
                <List dense disablePadding>
                  {group.fields.map((field) => (
                    <ListItem key={field.name} sx={{ py: 0 }}>
                      <ListItemText
                        primary={field.label}
                        slotProps={{ primary: { variant: "body2" } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function ClaimFormPreviewModal({ open, onClose, claimForm }) {
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }

    setOpenCD(true);
    setTca({
      title: `Vorschau: ${claimForm?.label ?? ""}`,
      maxWidth: "lg",
      content: <ClaimFormPreviewContent claimForm={claimForm} />,
      actions: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, claimForm?.id]);

  return (
    <ContentDialog openCD={openCD} setOpenCD={setOpenCD} tca={tca} onClose={onClose} />
  );
}
